import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';
import { 
  Play, Instagram, Video, ChevronLeft, ChevronRight, X, ExternalLink, Youtube
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Extract Instagram Reel ID from URL
const extractInstagramId = (url) => {
  const match = url?.match(/instagram\.com\/(?:reel|p)\/([A-Za-z0-9_-]+)/);
  return match ? match[1] : null;
};

// Extract YouTube video ID from URL
const extractYoutubeId = (url) => {
  const match = url?.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]+)/);
  return match ? match[1] : null;
};

// Get YouTube thumbnail URL
const getYoutubeThumbnail = (videoUrl) => {
  const videoId = extractYoutubeId(videoUrl);
  if (videoId) {
    // Try maxresdefault first, fallback handled by onError
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  }
  return null;
};

// Get thumbnail with fallback
const getVideoThumbnail = (video) => {
  // If custom thumbnail is set, use it
  if (video.thumbnail) return video.thumbnail;
  
  // For YouTube, auto-generate thumbnail
  if (video.type === 'youtube') {
    return getYoutubeThumbnail(video.video_url);
  }
  
  // For Instagram, we can't auto-get thumbnail, return null
  return null;
};

export const VideosShowcase = ({ 
  maxVideos = 6, 
  showTitle = true,
  className = "" 
}) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [previewModal, setPreviewModal] = useState({ open: false, video: null });

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`${API}/videos`);
        setVideos(response.data.slice(0, maxVideos));
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, [maxVideos]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % Math.max(1, videos.length - 2));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + Math.max(1, videos.length - 2)) % Math.max(1, videos.length - 2));
  };

  const openPreview = (video) => {
    setPreviewModal({ open: true, video });
  };

  const closePreview = () => {
    setPreviewModal({ open: false, video: null });
  };

  if (loading) {
    return (
      <div className={`py-16 ${className}`} data-testid="videos-showcase-loading">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-4 overflow-hidden">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex-shrink-0 w-64 aspect-[9/16] bg-slate-200 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (videos.length === 0) {
    return null;
  }

  return (
    <section className={`py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 ${className}`} data-testid="videos-showcase">
      <div className="max-w-7xl mx-auto px-4">
        {showTitle && (
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10">
            <div>
              <p className="text-pink-400 font-semibold mb-2 flex items-center gap-2">
                <Instagram className="w-4 h-4" /> OUR WORK VIDEOS
              </p>
              <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-white mb-4">
                Watch Our Recent Projects
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full"></div>
            </div>
            {videos.length > 3 && (
              <div className="flex items-center gap-2 mt-4 md:mt-0">
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={prevSlide}
                  className="rounded-full border-white/30 text-white hover:bg-white/10"
                  data-testid="videos-prev"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={nextSlide}
                  className="rounded-full border-white/30 text-white hover:bg-white/10"
                  data-testid="videos-next"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Videos Carousel */}
        <div className="overflow-hidden">
          <div 
            className="flex gap-4 transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentSlide * 280}px)` }}
          >
            {videos.map((video) => {
              const thumbnail = getVideoThumbnail(video);
              return (
              <div 
                key={video.id}
                className="flex-shrink-0 w-64 aspect-[9/16] relative rounded-xl overflow-hidden group cursor-pointer bg-slate-800"
                onClick={() => openPreview(video)}
                data-testid={`video-card-${video.id}`}
              >
                {/* Thumbnail */}
                {thumbnail ? (
                  <img 
                    src={thumbnail} 
                    alt={video.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      // Fallback for YouTube if maxres not available
                      if (video.type === 'youtube') {
                        const videoId = extractYoutubeId(video.video_url);
                        e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                      }
                    }}
                  />
                ) : video.type === 'instagram' ? (
                  <div className="w-full h-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex flex-col items-center justify-center">
                    <Instagram className="w-16 h-16 text-white mb-2" />
                    <span className="text-white/80 text-sm font-medium">Instagram Reel</span>
                  </div>
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 flex items-center justify-center">
                    <Video className="w-16 h-16 text-white/30" />
                  </div>
                )}

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center transform scale-90 group-hover:scale-100 transition-transform">
                    <Play className="w-8 h-8 text-slate-800 ml-1" />
                  </div>
                </div>

                {/* Type badge */}
                <div className="absolute top-3 left-3">
                  {video.type === 'instagram' && (
                    <span className="px-2.5 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium rounded-full flex items-center gap-1 shadow-lg">
                      <Instagram className="w-3 h-3" /> Reel
                    </span>
                  )}
                  {video.type === 'youtube' && (
                    <span className="px-2.5 py-1 bg-red-500 text-white text-xs font-medium rounded-full flex items-center gap-1 shadow-lg">
                      <Play className="w-3 h-3" /> YouTube
                    </span>
                  )}
                </div>

                {/* Title overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
                  <h3 className="text-white font-semibold text-sm line-clamp-2">{video.title}</h3>
                  {video.category && (
                    <p className="text-slate-300 text-xs mt-1">{video.category}</p>
                  )}
                </div>
              </div>
            )})}
          </div>
        </div>

        {/* Slider dots */}
        {videos.length > 3 && (
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: Math.max(1, videos.length - 2) }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentSlide 
                    ? 'bg-gradient-to-r from-pink-500 to-purple-500 w-8' 
                    : 'bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Video Preview Modal */}
      <Dialog open={previewModal.open} onOpenChange={(open) => !open && closePreview()}>
        <DialogContent className="max-w-lg p-0 overflow-hidden bg-black border-0">
          {previewModal.video && (
            <div className="relative">
              {/* Close button */}
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 z-10 text-white hover:bg-white/20"
                onClick={closePreview}
              >
                <X className="w-5 h-5" />
              </Button>

              {/* Instagram embed */}
              {previewModal.video.type === 'instagram' && (
                <div className="aspect-[9/16] w-full">
                  <iframe
                    src={`https://www.instagram.com/reel/${extractInstagramId(previewModal.video.video_url)}/embed`}
                    className="w-full h-full"
                    frameBorder="0"
                    allowFullScreen
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                  />
                </div>
              )}

              {/* YouTube embed */}
              {previewModal.video.type === 'youtube' && (
                <div className="aspect-video w-full">
                  <iframe
                    src={`https://www.youtube.com/embed/${extractYoutubeId(previewModal.video.video_url)}?autoplay=1`}
                    className="w-full h-full"
                    frameBorder="0"
                    allowFullScreen
                    allow="autoplay; encrypted-media"
                  />
                </div>
              )}

              {/* Direct video */}
              {previewModal.video.type === 'direct' && (
                <video 
                  src={previewModal.video.video_url} 
                  controls 
                  autoPlay
                  className="w-full"
                />
              )}

              {/* Footer with title and link */}
              <div className="p-4 bg-slate-900">
                <h3 className="text-white font-semibold">{previewModal.video.title}</h3>
                {previewModal.video.description && (
                  <p className="text-slate-400 text-sm mt-1">{previewModal.video.description}</p>
                )}
                <a 
                  href={previewModal.video.video_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-pink-400 hover:text-pink-300 text-sm mt-2"
                >
                  <ExternalLink className="w-4 h-4" /> View Original
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default VideosShowcase;
